import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ahtApi } from '../../network/apis/aht';
import FormInput from '../../components/elements/form_input';
import Button from '../../components/elements/button';
// import FileUpload from '../../components/elements/file_upload';
import '../../assets/css/doctor/profile.css'; // Reusing doctor profile styles

const AHTProfile = () => {
    const navigate = useNavigate();
    const [ahtProfile, setAHTProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [formData, setFormData] = useState({
        specialization: '',
        experience: '',
        coverage_area: '',
        coverage_address: '',
        working_hours_from: '',
        working_hours_to: '',
        consultation_fee: '',
        phone: '',
        address: '',
        city: '',
        state: '',
    });

    useEffect(() => {
        fetchAHTProfile();
    }, []);

    const fetchAHTProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await ahtApi.getAHTProfile();
            const profile = response?.data?.data || response?.data;
            setAHTProfile(profile);
            
            // Pre-fill form data
            if (profile) {
                setFormData({
                    specialization: profile.specialization || '',
                    experience: profile.experience || '',
                    coverage_area: profile.coverage_area || '',
                    coverage_address: profile.coverage_address || '',
                    working_hours_from: profile.working_hours_from || '',
                    working_hours_to: profile.working_hours_to || '',
                    consultation_fee: profile.consultation_fee || '',
                    phone: profile.user_details?.phone || '',
                    address: profile.address || '',
                    city: profile.city || '',
                    state: profile.state || '',
                });
            }
        } catch (err) {
            console.error('Failed to fetch AHT profile:', err);
            setError(err.message || 'Failed to fetch profile');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsSaving(true);

        try {
            const updateData = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    updateData.append(key, formData[key]);
                }
            });

            await ahtApi.updateAHT(ahtProfile.id, updateData);
            setSuccess('Profile updated successfully!');
            setIsEditing(false);
            
            // Refresh profile
            setTimeout(() => fetchAHTProfile(), 1500);
        } catch (err) {
            console.error('Failed to update profile:', err);
            setError(err.message || 'Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <div className="profile-container"><h2>Loading profile...</h2></div>;
    }

    if (error && !ahtProfile) {
        return (
            <div className="profile-container">
                <div className="alert alert-error">{error}</div>
                <button onClick={fetchAHTProfile}>Retry</button>
            </div>
        );
    }

    if (!ahtProfile) {
        return (
            <div className="profile-container">
                <div className="alert alert-warning">Profile not found</div>
                <button onClick={() => navigate('/aht')}>Go to Dashboard</button>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <div className="profile-header">
                <h1>My Profile</h1>
                {!isEditing && (
                    <button 
                        className="edit-btn"
                        onClick={() => setIsEditing(true)}
                    >
                        Edit Profile
                    </button>
                )}
            </div>

            {error && (
                <div className="alert alert-error">{error}</div>
            )}

            {success && (
                <div className="alert alert-success">{success}</div>
            )}

            {/* Profile Overview */}
            {!isEditing && (
                <div className="profile-view">
                    <div className="profile-card">
                        <div className="profile-header-info">
                            <h2>{ahtProfile.user_details?.first_name} {ahtProfile.user_details?.last_name}</h2>
                            <p>{ahtProfile.specialization}</p>
                            <span className={`verification-badge ${ahtProfile.is_verified ? 'verified' : 'pending'}`}>
                                {ahtProfile.is_verified ? '✓ Verified' : '⋯ Pending Verification'}
                            </span>
                        </div>

                        <div className="info-section">
                            <h3>Professional Information</h3>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>License Number</label>
                                    <p>{ahtProfile.license_number}</p>
                                </div>
                                <div className="info-item">
                                    <label>Specialization</label>
                                    <p>{ahtProfile.specialization}</p>
                                </div>
                                <div className="info-item">
                                    <label>Experience</label>
                                    <p>{ahtProfile.experience} years</p>
                                </div>
                                <div className="info-item">
                                    <label>Consultation Fee</label>
                                    <p>₦{parseFloat(ahtProfile.consultation_fee).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <h3>Service Area</h3>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Coverage Area</label>
                                    <p>{ahtProfile.coverage_area}</p>
                                </div>
                                <div className="info-item">
                                    <label>Address</label>
                                    <p>{ahtProfile.coverage_address}</p>
                                </div>
                                <div className="info-item">
                                    <label>Working Hours</label>
                                    <p>{ahtProfile.working_hours_from} - {ahtProfile.working_hours_to}</p>
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <h3>Contact Information</h3>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Email</label>
                                    <p>{ahtProfile.user_details?.email}</p>
                                </div>
                                <div className="info-item">
                                    <label>Phone</label>
                                    <p>{ahtProfile.user_details?.phone}</p>
                                </div>
                            </div>
                        </div>

                        {ahtProfile.qualifications && ahtProfile.qualifications.length > 0 && (
                            <div className="info-section">
                                <h3>Qualifications</h3>
                                <div className="list">
                                    {ahtProfile.qualifications.map((qual, idx) => (
                                        <div key={idx} className="list-item">
                                            <p><strong>{qual.degree}</strong></p>
                                            <p>{qual.institution} ({qual.year})</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {ahtProfile.certifications && ahtProfile.certifications.length > 0 && (
                            <div className="info-section">
                                <h3>Certifications</h3>
                                <div className="list">
                                    {ahtProfile.certifications.map((cert, idx) => (
                                        <div key={idx} className="list-item">
                                            <p><strong>{cert.name}</strong></p>
                                            <p>{cert.issuing_authority} ({cert.year})</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Profile Edit Form */}
            {isEditing && (
                <form onSubmit={handleSaveProfile} className="profile-edit">
                    <div className="form-section">
                        <h3>Edit Professional Information</h3>
                        <FormInput
                            label="Specialization"
                            type="text"
                            value={formData.specialization}
                            onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                        />
                        <FormInput
                            label="Years of Experience"
                            type="number"
                            value={formData.experience}
                            onChange={(e) => setFormData({...formData, experience: e.target.value})}
                        />
                        <FormInput
                            label="Consultation Fee"
                            type="number"
                            value={formData.consultation_fee}
                            onChange={(e) => setFormData({...formData, consultation_fee: e.target.value})}
                        />
                    </div>

                    <div className="form-section">
                        <h3>Edit Service Area</h3>
                        <FormInput
                            label="Coverage Area"
                            type="text"
                            value={formData.coverage_area}
                            onChange={(e) => setFormData({...formData, coverage_area: e.target.value})}
                        />
                        <FormInput
                            label="Service Address"
                            type="text"
                            value={formData.coverage_address}
                            onChange={(e) => setFormData({...formData, coverage_address: e.target.value})}
                        />
                        <FormInput
                            label="Working Hours From"
                            type="time"
                            value={formData.working_hours_from}
                            onChange={(e) => setFormData({...formData, working_hours_from: e.target.value})}
                        />
                        <FormInput
                            label="Working Hours To"
                            type="time"
                            value={formData.working_hours_to}
                            onChange={(e) => setFormData({...formData, working_hours_to: e.target.value})}
                        />
                    </div>

                    <div className="form-section">
                        <h3>Edit Contact Information</h3>
                        <FormInput
                            label="Phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                        <FormInput
                            label="City"
                            type="text"
                            value={formData.city}
                            onChange={(e) => setFormData({...formData, city: e.target.value})}
                        />
                        <FormInput
                            label="State"
                            type="text"
                            value={formData.state}
                            onChange={(e) => setFormData({...formData, state: e.target.value})}
                        />
                    </div>

                    <div className="form-actions">
                        <Button
                            type="button"
                            label="Cancel"
                            onClick={() => setIsEditing(false)}
                            style={{
                                backgroundColor: '#757575',
                                color: 'white'
                            }}
                        />
                        <Button
                            type="submit"
                            label={isSaving ? "Saving..." : "Save Changes"}
                            disabled={isSaving}
                            style={{
                                backgroundColor: '#4CAF50',
                                color: 'white'
                            }}
                        />
                    </div>
                </form>
            )}
        </div>
    );
};

export default AHTProfile;
